import { TrackerEmail, EmailAttachment } from '../../tracker/types/tracker.types';

/**
 * Checks if an email is a voicemail message
 * Voicemails have subjects starting with "Message vocal" and contain MP3 attachments
 */
export function isVoicemail(email: TrackerEmail): boolean {
  if (!email.subject?.startsWith('Message vocal')) {
    return false;
  }

  const hasMp3Attachment = email.attachments?.some(
    (attachment) => {
      // Check both camelCase and snake_case variants (API uses snake_case)
      const contentType = attachment.contentType || attachment.content_type || '';
      return (
        contentType.toLowerCase().includes('audio/mpeg') ||
        contentType.toLowerCase().includes('audio/mp3') ||
        attachment.name?.toLowerCase().endsWith('.mp3')
      );
    }
  );

  return hasMp3Attachment || false;
}

/**
 * Gets the MP3 attachment from a voicemail email
 */
export function getVoicemailAttachment(email: TrackerEmail): EmailAttachment | null {
  if (!isVoicemail(email) || !email.attachments) {
    return null;
  }

  const mp3Attachment = email.attachments.find(
    (attachment) =>
      attachment.contentType?.toLowerCase().includes('audio/mpeg') ||
      attachment.contentType?.toLowerCase().includes('audio/mp3') ||
      attachment.name?.toLowerCase().endsWith('.mp3')
  );

  return mp3Attachment || null;
}

/**
 * Extracts the caller phone number from voicemail subject
 * Subject format: Message vocal de "+32471860672" <+32471860672>
 */
export function getVoicemailCaller(email: TrackerEmail): string | null {
  if (!isVoicemail(email) || !email.subject) {
    return null;
  }

  // Extract phone number from subject
  const match = email.subject.match(/[""]([+\d]+)[""]/);
  return match ? match[1] : null;
}

/**
 * Normalizes a phone number for database search
 * Converts formats like +32484737252 to 0484737252
 * Belgian numbers: +32 -> 0, +33 -> skip
 */
export function normalizePhoneNumber(phone: string): string[] {
  if (!phone) return [];

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  const variants: string[] = [];

  // Belgian number with +32
  if (cleaned.startsWith('+32')) {
    const withoutCountryCode = '0' + cleaned.substring(3);
    variants.push(withoutCountryCode);
    variants.push(cleaned); // Also try the original +32 format
  }
  // Already in 0... format
  else if (cleaned.startsWith('0')) {
    variants.push(cleaned);
    // Also try with +32
    variants.push('+32' + cleaned.substring(1));
  }
  // International format without +
  else if (cleaned.startsWith('32')) {
    variants.push('0' + cleaned.substring(2));
    variants.push('+' + cleaned);
  }
  // Other formats
  else {
    variants.push(cleaned);
  }

  return variants;
}
